import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Col, Container, Div, Icon, Row } from '@pinpoint/design-kit';

import { Button, Message } from 'src/components';
import EqualBucketsSelector from 'src/components/models/equal_buckets_selector';
import { appActions, modelViewActions } from 'src/store/actions';
import { Page } from 'src/containers';
import CONSTANTS from 'src/store/constants';
import { ROUTES } from 'src/config';

import RoiCalculator from 'src/components/models/charts/roi_calculator';
import { ActuarialRoiCalculator } from 'src/components/models/charts/actuarial_calculator';
import { HistoryProp, ModelProp } from 'src/store/typings';

function ModelsRoiCalculator({
  history,
  location,
  match,
  app,
  modelView,
  modelView: { equalType },
  fetchModel,
  enqueueNotification,
  saveRoiCalcState,
  exportRoiCalculator,
  initRoiCalcState,
  resetRoiCalcStateField,
  updateRoiCalcState,
  setReadyExport,
  setEqualType,
  resetPage,
}) {
  const {
    params: { modelClass, modelId },
  } = match;

  const showRoiCalc = useMemo(
    () => (modelView?.model?.dataModelType || modelView?.model?.data_model_type || '').includes('ACT_'),
    [modelView]
  );

  const getData = useCallback(() => {
    if (modelId) fetchModel(modelId);
  }, [modelId, fetchModel]);

  const isLiftChartBucket10Generated = (modelView?.availableQuantiles?.current ?? []).includes(10);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (modelView.exportRoiCalculatorStatus === CONSTANTS.API.STARTED)
      enqueueNotification('Calculator export has begun.', { autoClose: true });
    if (modelView.exportRoiCalculatorStatus === CONSTANTS.API.FAILED)
      enqueueNotification('An error occurred when generating the export. Please notify Pinpoint.', { autoClose: true });
  }, [modelView.exportRoiCalculatorStatus, enqueueNotification]);

  return (
    <Container
      w="100%"
      minW="100%"
      h="100%"
      minH="100%"
      data-debug="container"
      d="flex"
      flexDir="column"
      overflow="hidden"
    >
      <Row data-debug="nav-row" p={{ t: '1.5rem' }} m={{ x: '.5rem' }}>
        <Col gutterWidth="0" d="inline-flex" size="12" align="center">
          <Button
            buttonStyle="primary"
            onClick={() => {
              history.push(ROUTES.MODELS);
              resetPage();
            }}
          >
            <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
            BACK
          </Button>
          <Div data-test="lift-charts-btn">
            <Button
              buttonStyle="primary"
              data-tip="View lift charts for model"
              onClick={() => {
                history.push(ROUTES.MODEL_LIFT_CHARTS.replace(':modelId', modelId).replace(':modelClass', modelClass));
              }}
            >
              <Icon name="BarChartEnclosed" size="1rem" color="white" viewBox="0 0 16 17" m={{ r: '.5rem' }} />
              LIFT CHARTS
            </Button>
          </Div>
          {showRoiCalc && (
            <Div data-test="export-btn-div">
              <Button
                buttonStyle="primary"
                data-test="export-btn"
                onClick={exportRoiCalculator}
                disabled={!modelView.exportRoiCalculatorReady}
                data-tip={
                  !modelView.exportRoiCalculatorReady
                    ? 'Enter all inputs and press "Calculate" first'
                    : 'Download your full calculator in Excel'
                }
              >
                <Icon name="Excel" color="white" size="1rem" viewBox="0 0 21 22" m={{ r: '.5rem' }} />
                SAVE IN EXCEL
              </Button>
            </Div>
          )}
          <EqualBucketsSelector
            models={[modelView?.model]}
            equalType={equalType}
            handleEqualTypeChange={setEqualType}
          />
        </Col>
      </Row>

      {!isLiftChartBucket10Generated ? (
        <Message default type="info" text="Impact Calculator unavailable" />
      ) : (
        <Row
          data-debug="content"
          p={{ t: '.5rem' }}
          m={{ x: '.5rem' }}
          h="100%"
          maxH="100%"
          minH="0px"
          justify="stretch"
          align="stretch"
        >
          {
            {
              [CONSTANTS.API.SUCCEEDED]: (
                <Col
                  gutterWidth="0"
                  size="12"
                  bg="white"
                  rounded="md"
                  overflow="hidden"
                  d="flex"
                  flexDir="column"
                  data-test="paper"
                  minH="0px"
                  h="100%"
                  maxH="100%"
                >
                  {modelClass === 'actuarial' && (
                    <ActuarialRoiCalculator
                      app={app}
                      history={history}
                      initRoiCalcState={initRoiCalcState}
                      location={location}
                      modelView={modelView}
                      resetRoiCalcStateField={resetRoiCalcStateField}
                      saveRoiCalcState={saveRoiCalcState}
                      updateRoiCalcState={updateRoiCalcState}
                      setReadyExport={setReadyExport}
                    />
                  )}
                  {modelClass === 'classifier' && (
                    <RoiCalculator
                      app={app}
                      history={history}
                      initRoiCalcState={initRoiCalcState}
                      location={location}
                      modelView={modelView}
                      resetRoiCalcStateField={resetRoiCalcStateField}
                      saveRoiCalcState={saveRoiCalcState}
                      updateRoiCalcState={updateRoiCalcState}
                      setReadyExport={setReadyExport}
                    />
                  )}
                </Col>
              ),
              [CONSTANTS.API.FAILED]: <Message type="error" />,
              [CONSTANTS.API.STARTED]: <Message />,
              default: <Message type="info" text="No data" cta={{ text: 'Refresh', onClick: getData }} />,
            }[modelView.fetchModelStatus]
          }
        </Row>
      )}
    </Container>
  );
}

const mapStateToProps = ({ app, modelView }, ownProps) => ({
  app,
  modelView,
  ...ownProps,
});

const mapDispatchToProps = (dispatch) => ({
  fetchModel: (...params) => dispatch(modelViewActions.fetchModel(...params)),
  setChart: (...params) => dispatch(modelViewActions.setChart(...params)),
  setGradient: (...params) => dispatch(modelViewActions.setGradient(...params)),
  fetchDownloadUrl: (...params) => dispatch(modelViewActions.fetchDownloadUrl(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
  saveRoiCalcState: (...params) => dispatch(modelViewActions.saveRoiCalculatorState(...params)),
  exportRoiCalculator: () => dispatch(modelViewActions.exportRoiCalculator()),
  initRoiCalcState: (...params) => dispatch(modelViewActions.initRoiCalculatorState(...params)),
  updateRoiCalcState: (key, value) => dispatch(modelViewActions.updateRoiCalculatorState(key, value)),
  resetRoiCalcStateField: (key) => dispatch(modelViewActions.resetRoiCalculatorStateField(key)),
  setReadyExport: (nextState) => dispatch(modelViewActions.setReadyExport(nextState)),
  setEqualType: (key) => dispatch(modelViewActions.setEqualType(key)),
  resetPage: () => dispatch(modelViewActions.resetPage()),
});

ModelsRoiCalculator.propTypes = {
  history: HistoryProp.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      modelClass: PropTypes.string.isRequired,
      modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  app: PropTypes.shape({
    profile: PropTypes.shape({
      tenant: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
  }).isRequired,
  modelView: PropTypes.shape({
    availableQuantiles: PropTypes.shape({
      current: PropTypes.oneOf([undefined, PropTypes.array]).isRequired,
    }).isRequired,
    equalType: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    model: ModelProp,
    fetchModelStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    exportRoiCalculatorStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    exportRoiCalculatorReady: PropTypes.bool.isRequired,
  }).isRequired,
  fetchModel: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  saveRoiCalcState: PropTypes.func.isRequired,
  exportRoiCalculator: PropTypes.func.isRequired,
  initRoiCalcState: PropTypes.func.isRequired,
  resetRoiCalcStateField: PropTypes.func.isRequired,
  updateRoiCalcState: PropTypes.func.isRequired,
  setReadyExport: PropTypes.func.isRequired,
  setEqualType: PropTypes.func.isRequired,
  resetPage: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(ModelsRoiCalculator));
